/**
 * Parser module.
 * @module lib/Parser
 * Takes changes from codeMirror onChange event and parses it into a map
 * Each key of the map corresponds to the line number of the code in CodeMirror
 */
import {
  type AllModifiersType,
  type LineObject,
  LinkModifiers,
  type ModifierArrayKeys,
  type NavigationTypeType,
  type ParsedQpStack,
  allModifiers,
  codeBlockControlChar,
  codeBlockRegEx,
  controlChars,
  defaultNavigationIndex,
  isControlChar,
  isKeyword,
  keywordAndModifierRegex,
  keywords,
  moveByOptions
} from "@quickpoint.me/shared";
// TODO: https://github.com/remarkablemark/html-react-parser/issues/94
import { sendNavigationTypeToAuthor, sendParsedStackToAuthor, sendStoryNamesToAuthor } from "../functionalComponents/MessageHandler";






function isMoveBy(value: unknown): value is typeof moveByOptions[number] {
  return moveByOptions.includes(value as typeof moveByOptions[number]);
}

function checkForControlChar(line: string) {
  line = line.trim();
  if (controlChars.some((el) => el === line)) return true
  return false
}

function checkForCodeBlock(line: string) {
  const test = codeBlockRegEx.exec(line)
  if (test) return true
  return false
}

function addModifier(
  page: LineObject[],
  lineIdx: number,
  type: string,
  content: string,
) {

  if (!page[lineIdx].modifier) {
    page[lineIdx].modifier = { links: [] }
  }
  // a modifier can have a url: key and also an array of links: [{url: "a url"},...]
  if (LinkModifiers.find(t => t === type)) {
    const t = type as ModifierArrayKeys
    const link = { type: t, link: content.trim() }
    //@ts-expect-error: why? is it not handled in line 45
    page[lineIdx].modifier.links.push(link)

  }
  const t = type as AllModifiersType
  page[lineIdx].modifier[t] = content
  return page;
}
/**
 * Parse the event
 * @param {array} code - A string of rYaml
 * @returns {object} An object that describes the structure of the page
 */

const parser = (code: string): ParsedQpStack => {
  if (!code) {
    return [];
  }
  const storyNames: string[] = []
  const lines = code.split("\n");
  let content: string;
  let navigationType: NavigationTypeType = moveByOptions[defaultNavigationIndex]
  console.log("Parsing code with", lines.length, "lines");


  let page: LineObject[] = [];
  // init Stack with index 0 filled. Pages are counted from base 1 so this should keep page numbering and
  // referencing consistent between the author and the viewer

  const stack: ParsedQpStack = [];

  let lineIdx = 0;
  let inCodeBlock = false
  lines.forEach((line: string, idx: number) => {
    const keywordMatch = line.match(keywordAndModifierRegex);
    const controlCharsMatch = checkForControlChar(line);
    let isIndented = false;

    // deal with code blocks. Munge all lines into one until the code block ends.
    if (checkForCodeBlock(line)) {
      inCodeBlock = !inCodeBlock
      if (inCodeBlock) {
        content = ""
        page[lineIdx] = { type: codeBlockControlChar, content, modifier: {}, lineNumber: idx };
      }
    }

    if (inCodeBlock) {
      content = `${content + line}\n`
      if (idx < lines.length - 1) return
    }


    let type;
    if (keywordMatch?.[2]) {
      if (keywordMatch[1]) isIndented = true;
      type = keywordMatch[2];
      content = keywordMatch[3 + keywords.length + allModifiers.length];
      content = content.replace(type, "");
      content = content.trim();
    }

    // if its a control character give it it's own line
    if (controlCharsMatch) {
      type = line;
      content = "";
    }

    // In the case where it's not indented and it's not a keyword default to paragraph:
    // but if there's no content, i.e. a blank line.. insert a blank:
    if (!isIndented && !isKeyword(type) && !isControlChar(type) && !inCodeBlock && line !== codeBlockControlChar) {
      if (line.trim() === "") {
        page[lineIdx] = { type: "blank:", content: "", lineNumber: idx, modifier: { links: [] } };
        lineIdx++;
        return;
      }
      content = line;
      type = "paragraph:";
    }


    if (type === "===") {
      // if it's a new page reset for new page and store the previous one.
      lineIdx = 0;
      // clone the array and add it to the stack
      stack.push(Array.from(page));
      // empty the page array
      page.length = 0;
      // reset the page divider count
      return;
    }

    if (type === "storyName:") {
      storyNames.push(content)
    }


    if (type === "moveBy:") {
      if (isMoveBy(content))
        navigationType = content as NavigationTypeType
    }

    if (isKeyword(type) || isControlChar(type)) {
      // otherwise create a new page element
      // @ts-expect-error: why aren't the above type guards being taken into account?
      page[lineIdx] = { type: type, content: "", lineNumber: idx };
    }

    // if it's indented then it's a modifier. Add it to the parent element and return
    const parentIdx = lineIdx - 1 < 0 ? 0 : lineIdx - 1;
    if (isIndented) {
      if (page[parentIdx] && type) {
        type = type.trim();
        content = content.trim();
        page = addModifier(page, parentIdx, type, content);
      }
      return;
    }


    page[lineIdx].content = content;

    lineIdx++;
  });
  // push the final parsed page to the stack.
  stack.push(Array.from(page));
  sendStoryNamesToAuthor("QP", storyNames)
  sendNavigationTypeToAuthor("QP", navigationType)
  sendParsedStackToAuthor("QP", stack)
  return stack;
};

export default parser;
